import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Collapse,
  Paper,
  Stack,
  Chip,
  Pagination,
} from "@mui/material";
import { tokens } from "../../../theme";
import superAdminService from "../../../services/SüperAdminServices/superAdminService";
import DataTable from "../../../components/SüperAdminComponents/DataTable";
import BusinessIcon from "@mui/icons-material/Business";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { IconButton, Tooltip } from "@mui/material";
import subscriptionService from "../../../services/subscriptionService";

const ORDER_TYPE_TR = {
  initial_purchase: "İlk satın alma",
  plan_upgrade: "Plan yükseltme",
  plan_downgrade: "Plan düşürme",
  addon_increase: "Ek kapasite +",
  addon_decrease: "Ek kapasite −",
  renewal: "Yenileme / güncelleme",
};

function formatMoneySa(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `₺${n.toFixed(2)}`;
}

function planLabelSa(p) {
  if (!p) return "—";
  return `${p.name} (${p.code})`;
}

export default function TenantsPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [tenantUsageDetail, setTenantUsageDetail] = useState(null);
  const [tenantOrders, setTenantOrders] = useState([]);
  const [tenantDetailsLoading, setTenantDetailsLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    active: "",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [superAdminPlans, setSuperAdminPlans] = useState([]);
  const [activationForm, setActivationForm] = useState({
    plan_code: "",
    addon_units: 0,
    billing_cycle: "monthly",
  });

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await superAdminService.getAllTenants({
        withMeta: true,
        params: appliedFilters,
        headers: {
          "x-bone-pageindex": pageIndex,
          "x-bone-limit": pageSize,
          "x-bone-sortby": sortBy,
          "x-bone-sortorder": sortOrder,
        },
      });
      setTenants(response.data || []);
      setTotalCount(Number(response.headers?.["x-total-count"] || 0));
      setTotalPages(Number(response.headers?.["x-total-pages"] || 1));
    } catch (error) {
      console.error("Error fetching tenants:", error);
      setSnackbar({
        open: true,
        message: "Error fetching tenants",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, pageIndex, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plansRes = await subscriptionService.getSuperAdminPlans();
        setSuperAdminPlans(plansRes?.plans || []);
      } catch (error) {
        setSnackbar({
          open: true,
          message: error?.response?.data?.error || "Failed to load plans",
          severity: "error",
        });
      }
    };
    fetchPlans();
  }, []);

  const handleView = async (tenant) => {
    setSelectedTenant(tenant);
    setViewDialogOpen(true);
    setTenantUsageDetail(null);
    setTenantOrders([]);
    setTenantDetailsLoading(true);
    try {
      const usageRes = await subscriptionService.getSuperAdminTenantUsage(tenant.id);
      const ordersRes = await subscriptionService.getSuperAdminTenantOrders(tenant.id);
      setTenantUsageDetail(usageRes || null);
      setTenantOrders(ordersRes?.orders || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.error || error?.response?.data?.message || "Failed to load tenant subscription details",
        severity: "error",
      });
    } finally {
      setTenantDetailsLoading(false);
    }
  };

  const monthIndexFromStart = (startIso, paidAtIso) => {
    if (!startIso || !paidAtIso) return "-";
    const start = new Date(startIso);
    const paid = new Date(paidAtIso);
    const startYM = start.getFullYear() * 12 + (start.getMonth() + 1);
    const paidYM = paid.getFullYear() * 12 + (paid.getMonth() + 1);
    const diff = paidYM - startYM;
    return diff >= 0 ? diff + 1 : "-";
  };

  const handleToggleStatus = (tenant) => {
    setSelectedTenant(tenant);
    setTargetStatus(!tenant.active);
    setActivationForm({
      plan_code: "",
      addon_units: 0,
      billing_cycle: "monthly",
    });
    setStatusDialogOpen(true);
  };

  const confirmToggleStatus = async () => {
    try {
      const payload = targetStatus
        ? {
            ...(activationForm.plan_code ? { plan_code: activationForm.plan_code } : {}),
            addon_units: Number(activationForm.addon_units || 0),
            billing_cycle: activationForm.billing_cycle === "yearly" ? "yearly" : "monthly",
          }
        : {};
      await superAdminService.updateTenantStatus(selectedTenant.id, targetStatus, payload);
      setSnackbar({
        open: true,
        message: `Tenant status updated to ${targetStatus ? "Active" : "Inactive"}`,
        severity: "success",
      });
      setStatusDialogOpen(false);
      setSelectedTenant(null);
      setTargetStatus(null);
      fetchTenants();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Error updating tenant status",
        severity: "error",
      });
    }
  };

  const columns = [
    { id: "name", label: "Name" },
    { id: "email", label: "Email" },
    { id: "phone_number", label: "Phone" },
    { id: "plan_type", label: "Plan Type" },
    {
      id: "active",
      label: "Status",
      format: (value) => (
        <Box
          sx={{
            display: "inline-block",
            px: 1.5,
            py: 0.5,
            borderRadius: "8px",
            backgroundColor: value ? colors.greenAccent[800] : colors.redAccent[800],
            color: "white",
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          {value ? "Active" : "Inactive"}
        </Box>
      ),
    },
    {
      id: "status_action",
      label: "Status Action",
      format: (value, row) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", paddingLeft: 3 }}>
          <Tooltip title={row.active ? "Deactivate Tenant" : "Activate Tenant"}>
            <IconButton
              size="medium"
              onClick={() => handleToggleStatus(row)}
              sx={{
                color: row.active ? colors.greenAccent[400] : colors.redAccent[400],
                backgroundColor: row.active 
                  ? `${colors.greenAccent[800]}20` 
                  : `${colors.redAccent[800]}20`,
                border: `2px solid ${row.active ? colors.greenAccent[600] : colors.redAccent[600]}`,
                padding: "8px",
                "&:hover": {
                  backgroundColor: row.active 
                    ? `${colors.greenAccent[700]}40` 
                    : `${colors.redAccent[700]}40`,
                  transform: "scale(1.1)",
                },
                transition: "all 0.3s ease",
              }}
            >
              {row.active ? <ToggleOnIcon sx={{ fontSize: 28 }} /> : <ToggleOffIcon sx={{ fontSize: 28 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const sanitizeFilters = useMemo(() => () => {
    const output = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) output[key] = value;
    });
    return output;
  }, [filters]);

  const applyFilters = () => {
    setPageIndex(0);
    setAppliedFilters(sanitizeFilters());
  };

  const resetFilters = () => {
    setFilters({
      name: "",
      email: "",
      active: "",
      dateFrom: "",
      dateTo: "",
    });
    setPageIndex(0);
    setAppliedFilters({});
  };

  const activeFilterCount = Object.keys(appliedFilters || {}).length;

  const getFilenameFromHeaders = (headers) => {
    const contentDisposition = headers?.["content-disposition"] || "";
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    return match?.[1] || `tenants-export-${Date.now()}.xlsx`;
  };

  const handleExcelExport = async () => {
    try {
      setExportLoading(true);
      const response = await superAdminService.exportExcel({
        resource: "tenants",
        filters: appliedFilters,
        sortBy,
        sortOrder,
      });
      const fileName = getFilenameFromHeaders(response.headers);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "Excel downloaded successfully", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Excel export failed", severity: "error" });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Box sx={{ 
      p: 3, 
      width: "100%", 
      minWidth: "100%", 
      maxWidth: "100%", 
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, width: "100%", minWidth: "100%" }}>
        <BusinessIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(135deg, ${colors.grey[100]} 0%, ${colors.blueAccent[200]} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Tenants Management
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<FilterListIcon />}
              endIcon={filtersOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              onClick={() => setFiltersOpen((prev) => !prev)}
              sx={{
                color: colors.grey[100],
                backgroundColor: filtersOpen ? colors.blueAccent[700] : colors.primary[300],
                "&:hover": { backgroundColor: filtersOpen ? colors.blueAccent[800] : colors.primary[200] },
              }}
            >
              Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
            </Button>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExcelExport} disabled={exportLoading}>
              {exportLoading ? "Exporting..." : "Export Excel"}
            </Button>
          </Stack>
        </Box>
      </Box>
      <Collapse in={filtersOpen} timeout={250} unmountOnExit>
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`, border: `1px solid ${colors.grey[800]}30` }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1.5, mb: 1.5 }}>
            <TextField label="Tenant Name" size="small" value={filters.name} onChange={(event) => setFilters((prev) => ({ ...prev, name: event.target.value }))} />
            <TextField label="Email" size="small" value={filters.email} onChange={(event) => setFilters((prev) => ({ ...prev, email: event.target.value }))} />
            <TextField select label="Status" size="small" value={filters.active} onChange={(event) => setFilters((prev) => ({ ...prev, active: event.target.value }))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </TextField>
            <TextField label="Date From" type="date" size="small" value={filters.dateFrom} onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label="Date To" type="date" size="small" value={filters.dateTo} onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))} InputLabelProps={{ shrink: true }} />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={applyFilters}>Apply Filters</Button>
            <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>Clear</Button>
          </Stack>
        </Paper>
      </Collapse>

      <DataTable
        columns={columns}
        data={tenants}
        onView={handleView}
        loading={loading}
        emptyMessage="No tenants found"
      />
      <Paper elevation={0} sx={{ mt: 2, p: 1.5, borderRadius: 2, background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`, border: `1px solid ${colors.grey[800]}30` }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={`Total ${totalCount}`} />
            <Chip size="small" label={`Page ${pageIndex + 1}/${totalPages}`} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField select label="Rows" size="small" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPageIndex(0); }} sx={{ minWidth: 90 }}>
              {[10, 20, 50, 100].map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
            </TextField>
            <TextField select label="Sort By" size="small" value={sortBy} onChange={(event) => { setSortBy(event.target.value); setPageIndex(0); }} sx={{ minWidth: 120 }}>
              <MenuItem value="createdAt">Created</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </TextField>
            <TextField select label="Direction" size="small" value={sortOrder} onChange={(event) => { setSortOrder(event.target.value); setPageIndex(0); }} sx={{ minWidth: 110 }}>
              <MenuItem value="DESC">DESC</MenuItem>
              <MenuItem value="ASC">ASC</MenuItem>
            </TextField>
          </Stack>
          <Pagination count={Math.max(totalPages, 1)} page={Math.min(pageIndex + 1, Math.max(totalPages, 1))} onChange={(_, page) => setPageIndex(page - 1)} shape="rounded" />
        </Box>
      </Paper>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.grey[100] }}>
          Tenant Details
        </DialogTitle>
        <DialogContent>
          {selectedTenant && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Name
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedTenant.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Email
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedTenant.email}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedTenant.phone_number}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Plan Type
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedTenant.plan_type}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Address
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedTenant.address}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Status
                </Typography>
                <Box
                  sx={{
                    display: "inline-block",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "8px",
                    backgroundColor: selectedTenant.active
                      ? colors.greenAccent[800]
                      : colors.redAccent[800],
                    color: "white",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  }}
                >
                  {selectedTenant.active ? "Active" : "Inactive"}
                </Box>
              </Grid>
              {selectedTenant.adminDoctors && selectedTenant.adminDoctors.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: colors.grey[400], mb: 1 }}>
                    Admin Doctors ({selectedTenant.adminDoctors.length})
                  </Typography>
                  {selectedTenant.adminDoctors.map((admin, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderRadius: "8px",
                        backgroundColor: colors.primary[600],
                        border: `1px solid ${colors.grey[800]}30`,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: colors.grey[100] }}>
                        {admin.name} {admin.surname} ({admin.email})
                      </Typography>
                      {admin.createdDoctors && admin.createdDoctors.length > 0 && (
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Created {admin.createdDoctors.length} doctor(s)
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 1.5, fontWeight: 700 }}>
                  Abonelik &amp; kullanım
                </Typography>

                {tenantDetailsLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={26} />
                  </Box>
                ) : tenantUsageDetail?.needs_checkout ? (
                  <Box>
                    <Box
                      sx={{
                        borderRadius: 2,
                        p: 2,
                        border: `1px dashed ${colors.grey[600]}`,
                        background: `linear-gradient(135deg, ${colors.primary[600]}18, transparent)`,
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: colors.grey[100], mb: 0.5 }}>
                        Aktif abonelik yok
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                        Kurum henüz paket satın almamış (checkout tamamlanmamış). Sipariş geçmişi varsa aşağıda listelenir.
                      </Typography>
                    </Box>
                    {(tenantOrders || []).length > 0 ? (
                      <Box sx={{ mt: 2, overflowX: "auto" }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: colors.grey[100], mb: 1 }}>
                          Sipariş geçmişi
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: colors.grey[400] }}>İşlem</TableCell>
                              <TableCell sx={{ color: colors.grey[400] }}>Plan değişimi</TableCell>
                              <TableCell align="right" sx={{ color: colors.grey[400] }}>
                                Tahsilat
                              </TableCell>
                              <TableCell sx={{ color: colors.grey[400] }}>Dönem</TableCell>
                              <TableCell sx={{ color: colors.grey[400] }}>Tarih</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(tenantOrders || []).map((o) => (
                              <TableRow key={o.id}>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  {ORDER_TYPE_TR[o.type] || o.type}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 200 }}>
                                  {planLabelSa(o.old_plan)} → {planLabelSa(o.new_plan)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                  {formatMoneySa(o.amount_tl)}
                                </TableCell>
                                <TableCell>
                                  {o.billing_cycle_snapshot === "yearly"
                                    ? "Yıllık"
                                    : o.billing_cycle_snapshot === "monthly"
                                      ? "Aylık"
                                      : "—"}
                                </TableCell>
                                <TableCell>
                                  {o.paid_at ? new Date(o.paid_at).toLocaleString("tr-TR") : "—"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    ) : null}
                  </Box>
                ) : tenantUsageDetail ? (
                  <Box
                    sx={{
                      background: `linear-gradient(155deg, ${colors.primary[600]}20, ${colors.primary[600]}08)`,
                      border: `1px solid ${colors.grey[800]}40`,
                      borderRadius: 2,
                      p: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: colors.grey[100], fontSize: "1.05rem" }}>
                          {tenantUsageDetail?.subscription?.Plan?.name || "—"} (
                          {tenantUsageDetail?.subscription?.Plan?.code || "—"})
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mt: 0.5 }}>
                          Başlangıç:{" "}
                          {tenantUsageDetail?.subscription?.starts_at
                            ? new Date(tenantUsageDetail.subscription.starts_at).toLocaleString("tr-TR")
                            : "—"}{" "}
                          · Faturalama:{" "}
                          {tenantUsageDetail?.subscription?.renewal_type === "yearly" ? "Yıllık" : "Aylık"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mt: 0.25 }}>
                          Sonraki yenileme:{" "}
                          {tenantUsageDetail?.subscription?.next_renewal_at
                            ? new Date(tenantUsageDetail.subscription.next_renewal_at).toLocaleString("tr-TR")
                            : "—"}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Referans aylık toplam
                        </Typography>
                        <Typography sx={{ fontWeight: 800, color: colors.greenAccent[400], fontSize: "1.1rem" }}>
                          {formatMoneySa(tenantUsageDetail?.subscription?.total_monthly_price_tl_snapshot)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                        gap: 1.25,
                        mb: 2,
                      }}
                    >
                      <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: `${colors.primary[600]}22` }}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Ek doktor slotu
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: colors.grey[100] }}>
                          +{tenantUsageDetail?.subscription?.addon_doctor_count ?? 0}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.grey[500], display: "block" }}>
                          Birim fiyat (snapshot):{" "}
                          {formatMoneySa(tenantUsageDetail?.subscription?.addon_price_per_doctor_tl_snapshot)}
                          /slot/ay
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: `${colors.primary[600]}22` }}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Ek depo / e-posta
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: colors.grey[100] }}>
                          +{tenantUsageDetail?.subscription?.addon_extra_storage_gb_total ?? 0} GB
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.grey[500] }}>
                          +{tenantUsageDetail?.subscription?.addon_extra_email_monthly_total ?? 0} e-posta/ay
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: `${colors.primary[600]}22` }}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Plan taban fiyat (snapshot)
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: colors.grey[100] }}>
                          {formatMoneySa(tenantUsageDetail?.subscription?.base_price_tl_snapshot)}
                          /ay
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Doktorlar: {tenantUsageDetail.usage.doctors_count} / {tenantUsageDetail.limits.doctor_limit}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          E-posta: {tenantUsageDetail.usage.emails_sent_count} /{" "}
                          {tenantUsageDetail.limits.monthly_email_limit} (kalan:{" "}
                          {tenantUsageDetail.derived.emails_remaining_this_month})
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Depo: {(tenantUsageDetail.usage.used_bytes / 1024 / 1024 / 1024).toFixed(2)} GB /{" "}
                          {tenantUsageDetail.limits.storage_limit_gb} GB
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Depo kalan:{" "}
                          {(tenantUsageDetail.derived.storage_remaining_bytes / 1024 / 1024 / 1024).toFixed(2)} GB
                        </Typography>
                      </Grid>
                    </Grid>

                    <Typography variant="body2" sx={{ fontWeight: 800, color: colors.grey[100], mb: 1 }}>
                      Doktor kırılımı
                    </Typography>
                    <Box sx={{ overflowX: "auto", mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: colors.grey[400] }}>Doktor</TableCell>
                            <TableCell sx={{ color: colors.grey[400] }}>Rol</TableCell>
                            <TableCell align="right" sx={{ color: colors.grey[400] }}>
                              E-posta
                            </TableCell>
                            <TableCell align="right" sx={{ color: colors.grey[400] }}>
                              Hasta
                            </TableCell>
                            <TableCell align="right" sx={{ color: colors.grey[400] }}>
                              Depo (GB)
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(tenantUsageDetail?.doctor_breakdown?.doctors || []).map((d) => (
                            <TableRow key={d.id}>
                              <TableCell sx={{ fontWeight: 700 }}>
                                {d.name} {d.surname}
                                <Typography variant="caption" sx={{ display: "block", color: colors.grey[400] }}>
                                  {d.email}
                                </Typography>
                              </TableCell>
                              <TableCell>{d.role}</TableCell>
                              <TableCell align="right">{d.emails_sent_count_this_month}</TableCell>
                              <TableCell align="right">{d.patients_count}</TableCell>
                              <TableCell align="right">
                                {(d.storage_used_bytes_net / 1024 / 1024 / 1024).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                          {(tenantUsageDetail?.doctor_breakdown?.doctors || []).length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                                  Doktor yok.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>

                    <Typography variant="body2" sx={{ fontWeight: 800, color: colors.grey[100], mb: 1 }}>
                      Sipariş &amp; ödeme geçmişi
                    </Typography>
                    <Box sx={{ overflowX: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: colors.grey[400] }}>Ay #*</TableCell>
                            <TableCell sx={{ color: colors.grey[400] }}>İşlem</TableCell>
                            <TableCell sx={{ color: colors.grey[400] }}>Önce → Sonra</TableCell>
                            <TableCell align="right" sx={{ color: colors.grey[400] }}>
                              Ek slot
                            </TableCell>
                            <TableCell align="right" sx={{ color: colors.grey[400] }}>
                              Ref. aylık
                            </TableCell>
                            <TableCell align="right" sx={{ color: colors.grey[400] }}>
                              Tahsilat
                            </TableCell>
                            <TableCell sx={{ color: colors.grey[400] }}>Dönem</TableCell>
                            <TableCell sx={{ color: colors.grey[400] }}>Kaynak</TableCell>
                            <TableCell sx={{ color: colors.grey[400] }}>Ödeme zamanı</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(tenantOrders || []).map((o) => (
                            <TableRow key={o.id}>
                              <TableCell>
                                {monthIndexFromStart(
                                  tenantUsageDetail?.subscription?.starts_at,
                                  o.paid_at
                                )}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {ORDER_TYPE_TR[o.type] || o.type}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 200 }}>
                                <Typography variant="caption" noWrap display="block" title={planLabelSa(o.old_plan)}>
                                  {planLabelSa(o.old_plan)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: colors.grey[500] }}>
                                  →
                                </Typography>
                                <Typography variant="caption" noWrap display="block" title={planLabelSa(o.new_plan)}>
                                  {planLabelSa(o.new_plan)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {o.old_addon_doctor_count ?? "—"} → {o.new_addon_doctor_count ?? "—"}
                              </TableCell>
                              <TableCell align="right">{formatMoneySa(o.monthly_total_tl_snapshot)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatMoneySa(o.amount_tl)}
                              </TableCell>
                              <TableCell>
                                {o.billing_cycle_snapshot === "yearly"
                                  ? "Yıllık"
                                  : o.billing_cycle_snapshot === "monthly"
                                    ? "Aylık"
                                    : "—"}
                              </TableCell>
                              <TableCell>{o.provider || "—"}</TableCell>
                              <TableCell>
                                {o.paid_at
                                  ? new Date(o.paid_at).toLocaleString("tr-TR")
                                  : "—"}
                              </TableCell>
                            </TableRow>
                          ))}
                          {(tenantOrders || []).length === 0 && (
                            <TableRow>
                              <TableCell colSpan={9}>
                                <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                                  Sipariş yok.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                    <Typography variant="caption" sx={{ color: colors.grey[500], display: "block", mt: 0.75 }}>
                      *Ay #, abonelik başlangıcına göre yaklaşık dönem sırasıdır.
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                    Veri yok.
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewDialogOpen(false)}
            sx={{ color: colors.grey[100] }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Tenant Status</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {selectedTenant && targetStatus !== null
              ? `Are you sure you want to ${targetStatus ? "activate" : "deactivate"} tenant "${selectedTenant.name}"?`
              : ""}
          </Typography>
          {targetStatus ? (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Alert severity="info">
                Aktivasyon sırasında paket ataması yapabilirsiniz. Tenant paket olmadan aktif edilemez.
              </Alert>
              <TextField
                select
                label="Plan"
                value={activationForm.plan_code}
                onChange={(event) =>
                  setActivationForm((prev) => ({ ...prev, plan_code: event.target.value }))
                }
                fullWidth
              >
                <MenuItem value="">Mevcut paketi koru</MenuItem>
                {superAdminPlans.map((plan) => (
                  <MenuItem key={plan.code} value={plan.code}>
                    {plan.name} ({plan.code})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="number"
                label="Addon Units"
                value={activationForm.addon_units}
                onChange={(event) =>
                  setActivationForm((prev) => ({
                    ...prev,
                    addon_units: Math.max(0, Number(event.target.value || 0)),
                  }))
                }
                inputProps={{ min: 0 }}
                fullWidth
              />
              <TextField
                select
                label="Billing Cycle"
                value={activationForm.billing_cycle}
                onChange={(event) =>
                  setActivationForm((prev) => ({ ...prev, billing_cycle: event.target.value }))
                }
                fullWidth
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </TextField>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmToggleStatus} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

